import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/lib/auth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ProtectedLayout } from "@/components/ProtectedLayout";
import { PageSkeleton } from "@/components/PageSkeleton";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Login from "./pages/Login";
import NotFound from "./pages/NotFound";

// Code splitting : chaque page = un chunk JS separe
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Prospection = lazy(() => import("./pages/Prospection"));
const TabTerrainHumain = lazy(() =>
  import("./pages/prospection/TabTerrainHumain").then((m) => ({ default: m.TabTerrainHumain })),
);
const TabCadastralAutomatise = lazy(() =>
  import("./pages/prospection/TabCadastralAutomatise").then((m) => ({ default: m.TabCadastralAutomatise })),
);
const Scraping = lazy(() => import("./pages/Scraping"));
const CRM = lazy(() => import("./pages/CRM"));
const Campaigns = lazy(() => import("./pages/Campaigns"));
const Messages = lazy(() => import("./pages/Messages"));
const MediaLibrary = lazy(() => import("./pages/MediaLibrary"));
const DPE = lazy(() => import("./pages/DPE"));
const SellerPortal = lazy(() => import("./pages/SellerPortal"));
const Financing = lazy(() => import("./pages/Financing"));
const Insurance = lazy(() => import("./pages/Insurance"));
const Settings = lazy(() => import("./pages/Settings"));
const Compliance = lazy(() => import("./pages/Compliance"));
const Referral = lazy(() => import("./pages/Referral"));
const Vitrine = lazy(() => import("./pages/Vitrine"));
const Visibility = lazy(() => import("./pages/Visibility"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const DebriefSession = lazy(() => import("./pages/DebriefSession"));
const SettingsJuliette = lazy(() => import("./pages/SettingsJuliette"));
const SettingsJulietteTon = lazy(() => import("./pages/SettingsJulietteTon"));
const SettingsJulietteAgence = lazy(() => import("./pages/SettingsJulietteAgence"));
const SettingsJulietteDocuments = lazy(() => import("./pages/SettingsJulietteDocuments"));
const SettingsJulietteConnexions = lazy(() => import("./pages/SettingsJulietteConnexions"));
const SettingsJulietteMemoire = lazy(() => import("./pages/SettingsJulietteMemoire"));
const SettingsJulietteConfidentialite = lazy(() => import("./pages/SettingsJulietteConfidentialite"));
const MorningBriefing = lazy(() => import("./pages/MorningBriefing"));
const PendingEmailReview = lazy(() => import("./pages/PendingEmailReview"));
const JulietteOnboarding = lazy(() => import("./pages/JulietteOnboarding"));
const JulietteHealthDashboard = lazy(() => import("./pages/admin/JulietteHealthDashboard"));
const JulietteTtsPoc = lazy(() => import("./pages/admin/JulietteTtsPoc"));
const JulietteBrain = lazy(() => import("./pages/admin/JulietteBrain"));
const UiKit = lazy(() => import("./pages/admin/UiKit"));
const JulietteContactsKnowledge = lazy(() => import("./pages/JulietteContactsKnowledge"));
const AmbassadorPublic = lazy(() => import("./pages/AmbassadorPublic"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NewsletterConfirm = lazy(() => import("./pages/NewsletterConfirm"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Ne pas retenter les erreurs d'authentification (JWT expire, etc.)
        if (error instanceof Error && error.message?.includes("JWT")) return false;
        const pgError = error as { code?: string };
        if (pgError.code === "PGRST301") return false;
        return failureCount < 1;
      },
      refetchOnReconnect: false,
    },
  },
});

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
    {/* reducedMotion="user" : toutes les animations framer-motion respectent
        prefers-reduced-motion (transforms neutralisés, fondus conservés). */}
    <MotionConfig reducedMotion="user">
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <ErrorBoundary>
            <Suspense fallback={<PageSkeleton />}>
            <Routes>
            {/* Public */}
            <Route path="/login" element={<Login />} />
            <Route path="/ambassador/:code" element={<AmbassadorPublic />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/newsletter/confirmer" element={<NewsletterConfirm />} />
            <Route path="/desinscription" element={<Unsubscribe />} />

            {/* Onboarding (WhatsApp setup) — layout propre, pas de sidebar */}
            <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
            <Route path="/debrief/:sessionId" element={<ProtectedRoute><DebriefSession /></ProtectedRoute>} />
            <Route path="/morning-briefing/:sessionId" element={<ProtectedRoute><MorningBriefing /></ProtectedRoute>} />
            <Route path="/pending-email/:id" element={<ProtectedRoute><PendingEmailReview /></ProtectedRoute>} />
            <Route path="/onboarding/juliette" element={<ProtectedRoute><JulietteOnboarding /></ProtectedRoute>} />

            {/* Routes protegees avec layout persistant (sidebar, tour, etc.) */}
            <Route element={<ProtectedLayout />}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/prospection" element={<Prospection />}>
                <Route index element={<Navigate to="terrain" replace />} />
                <Route path="terrain" element={<TabTerrainHumain />} />
                <Route path="cadastral" element={<TabCadastralAutomatise />} />
              </Route>
              <Route path="/scraping" element={<Scraping />} />
              <Route path="/crm" element={<CRM />} />
              <Route path="/crm/contact/:contactId" element={<CRM />} />
              <Route path="/campaigns" element={<Campaigns />} />
              <Route path="/campaigns/templates" element={<Campaigns />} />
              <Route path="/campaigns/branding" element={<Campaigns />} />
              <Route path="/messages" element={<Messages />} />
              <Route path="/media" element={<MediaLibrary />} />
              <Route path="/vitrine" element={<Vitrine />} />
              <Route path="/visibilite" element={<Visibility />} />
              <Route path="/referral" element={<Referral />} />
              <Route path="/dpe" element={<DPE />} />
              <Route path="/seller-portal" element={<SellerPortal />} />
              <Route path="/financing" element={<Financing />} />
              <Route path="/insurance" element={<Insurance />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/settings/juliette" element={<SettingsJuliette />} />
              <Route path="/settings/juliette/ton" element={<SettingsJulietteTon />} />
              <Route path="/settings/juliette/agence" element={<SettingsJulietteAgence />} />
              <Route path="/settings/juliette/documents" element={<SettingsJulietteDocuments />} />
              <Route path="/settings/juliette/connexions" element={<SettingsJulietteConnexions />} />
              <Route path="/settings/juliette/memoire" element={<SettingsJulietteMemoire />} />
              <Route path="/settings/juliette/confidentialite" element={<SettingsJulietteConfidentialite />} />
              {/* Redirections legacy : anciennes pages fusionnees dans /memoire */}
              <Route path="/settings/juliette/about" element={<Navigate to="/settings/juliette/memoire" replace />} />
              <Route path="/settings/juliette/insights" element={<Navigate to="/settings/juliette/memoire" replace />} />
              <Route path="/juliette/contacts-knowledge" element={<JulietteContactsKnowledge />} />
              <Route path="/admin/juliette-health" element={<JulietteHealthDashboard />} />
              <Route path="/admin/juliette-tts-poc" element={<JulietteTtsPoc />} />
              <Route path="/admin/juliette-brain" element={<JulietteBrain />} />
              <Route path="/admin/ui-kit" element={<UiKit />} />
              <Route path="/compliance" element={<Compliance />} />
            </Route>

            {/* Redirections de compatibilite (anciennes routes) */}
            <Route path="/radar" element={<Navigate to="/scraping" replace />} />
            <Route path="/sources" element={<Navigate to="/scraping" replace />} />
            <Route path="/contacts" element={<Navigate to="/crm" replace />} />
            <Route path="/pipeline" element={<Navigate to="/crm" replace />} />
            <Route path="/opportunities" element={<Navigate to="/crm" replace />} />
            <Route path="/properties" element={<Navigate to="/crm" replace />} />

            <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
            </ErrorBoundary>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
    </MotionConfig>
  </ThemeProvider>
);

export default App;
